/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createContext, useContext, useSyncExternalStore } from 'react';
import { DesktopAppStateStore, type DesktopWindowStateApi } from './DesktopAppStateStore';

/** The desktop's app state store. Stable for the desktop's lifetime, so it never re-renders. */
export const DesktopAppStateStoreContext = createContext<DesktopAppStateStore | null>(null);

export interface DesktopWindowScope {
  store: DesktopAppStateStore;
  windowId: string;
}

/** Set inside a desktop window; null on pages rendered outside the desktop (tests, Storybook). */
export const DesktopWindowScopeContext = createContext<DesktopWindowScope | null>(null);

export const useDesktopWindowScope = () => useContext(DesktopWindowScopeContext);

const subscribeNever = () => () => undefined;

/** Whether the enclosing window's saved state has loaded; always true outside a window. */
export function useDesktopWindowStateReady() {
  const scope = useDesktopWindowScope();
  return useSyncExternalStore(
    scope ? (listener) => scope.store.subscribe(listener) : subscribeNever,
    () => (scope ? scope.store.isReady(scope.windowId) : true),
  );
}

/** Whether saving window state to the server is failing (edits are kept and retried). */
export function useDesktopWindowStateSaveFailed() {
  const scope = useDesktopWindowScope();
  return useSyncExternalStore(
    scope ? (listener) => scope.store.subscribe(listener) : subscribeNever,
    () => scope?.store.hasSaveFailed() ?? false,
  );
}

// Outside a desktop window (tests, Storybook, standalone pages) state is shared in memory only.
const localWindowStateApi: DesktopWindowStateApi = {
  load: async () => ({}),
  save: async () => undefined,
  removeWindow: async () => undefined,
};

const createLocalScope = (): DesktopWindowScope => {
  const store = new DesktopAppStateStore(localWindowStateApi);
  void store.start(null);
  return { store, windowId: 'local' };
};

let localScope = createLocalScope();

/** Clears the in-memory state used outside desktop windows; called between tests. */
export const resetLocalDesktopState = () => {
  localScope = createLocalScope();
};

/** The enclosing window's scope, or the in-memory local scope outside a window. */
export const useDesktopStateScope = (): DesktopWindowScope => useDesktopWindowScope() ?? localScope;
